import { getTechnicalConfigToastError } from "features/shared/lib/technical-config-tab/tableHeaders";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type { TechnicalDataScenario, TechnicalDataScenarioFormState } from "../model/types";

import { TR_SCENARIO_CACHE_KEYS } from "../api/cacheKeys";
import { createTrScenario, deleteTrScenario, patchTrScenario } from "../api/callers";
import { useGetTrScenarios } from "../api/hooks";
import {
  createEmptyScenarioFormState,
  isDefaultScenario,
  mapFormStateToScenarioMetricsPayload,
} from "../lib/scenarioDefaults";
import { loadActiveScenarioId, saveActiveScenarioId } from "../lib/scenarioStorage";

type MutationError = { response?: { data?: unknown } };

export const useTechnicalDataScenarios = (projectId?: string) => {
  const queryClient = useQueryClient();
  const { data: scenarios = [], isLoading } = useGetTrScenarios(projectId);
  const [activeScenarioId, setActiveScenarioId] = useState<null | number>(() => loadActiveScenarioId(projectId));

  useEffect(() => {
    setActiveScenarioId((current) => {
      if (current != null && scenarios.some((scenario) => scenario.id === current)) return current;
      const stored = loadActiveScenarioId(projectId);
      if (stored != null && scenarios.some((scenario) => scenario.id === stored)) return stored;
      if (current != null && stored === current) return current;
      return scenarios.find((scenario) => scenario.isDefault)?.id ?? scenarios[0]?.id ?? null;
    });
  }, [projectId, scenarios]);

  useEffect(() => {
    saveActiveScenarioId(projectId, activeScenarioId);
  }, [activeScenarioId, projectId]);

  const invalidateScenarios = useCallback(
    () => queryClient.invalidateQueries(TR_SCENARIO_CACHE_KEYS.list(projectId)),
    [projectId, queryClient]
  );

  const createMutation = useMutation(
    (formState: TechnicalDataScenarioFormState) => {
      if (!projectId) throw new Error("Missing project");
      return createTrScenario(projectId, mapFormStateToScenarioMetricsPayload(formState));
    },
    {
      onError: (error: MutationError) => {
        toast.error(getTechnicalConfigToastError(error));
      },
      onSuccess: (created) => {
        setActiveScenarioId(created.id);
        return invalidateScenarios();
      },
    }
  );

  const updateMutation = useMutation(
    ({ formState, scenarioId }: { formState: TechnicalDataScenarioFormState; scenarioId: number }) => {
      if (!projectId) throw new Error("Missing project");
      return patchTrScenario(projectId, scenarioId, mapFormStateToScenarioMetricsPayload(formState));
    },
    {
      onError: (error: MutationError) => {
        toast.error(getTechnicalConfigToastError(error));
      },
      onSuccess: invalidateScenarios,
    }
  );

  const deleteMutation = useMutation(
    (scenarioId: number) => {
      if (!projectId) throw new Error("Missing project");
      return deleteTrScenario(projectId, scenarioId);
    },
    {
      onError: (error: MutationError) => {
        toast.error(getTechnicalConfigToastError(error));
      },
      onSuccess: invalidateScenarios,
    }
  );

  const activeScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === activeScenarioId) ?? scenarios[0],
    [activeScenarioId, scenarios]
  );

  const addScenario = useCallback(
    (formState: TechnicalDataScenarioFormState) => createMutation.mutateAsync(formState),
    [createMutation]
  );

  const updateScenario = useCallback(
    (scenarioId: number, formState: TechnicalDataScenarioFormState) =>
      updateMutation.mutateAsync({ formState, scenarioId }),
    [updateMutation]
  );

  const deleteScenario = useCallback(
    (scenarioId: number) => deleteMutation.mutateAsync(scenarioId),
    [deleteMutation]
  );

  const canDeleteScenario = (scenario: TechnicalDataScenario) => !isDefaultScenario(scenario);

  return {
    activeScenario,
    activeScenarioId,
    addScenario,
    canDeleteScenario,
    createEmptyFormState: createEmptyScenarioFormState,
    deleteScenario,
    isLoading,
    scenarios,
    setActiveScenarioId,
    updateScenario,
  };
};
